import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import InvoiceCreateWizard from "../../Finance/InvoiceCreateWizard";
import { apiUrl } from "../../ApiManager/AxiosHeaders";
import {
  ensureFinanceInvoiceApprovedState,
  normalizeFinanceInvoiceFromRecord,
  normalizeInvoiceNotesList,
  applyFinanceInvoiceMutationFromResponse,
  applyInvoiceNotesMutationFromResponse,
  canReverseInvoiceLedgerTransaction,
  resolveFinanceInvoiceTransactions,
  resolveInvoicePaidAmount,
} from "../../../utilis/invoiceEditHelpers";
import {
  buildFinanceInvoicePaymentFormData,
  buildFinanceInvoiceReversePaymentPayload,
  buildMultipartFormDataRequestConfig,
  getFinanceInvoicePaymentApiMessage,
  isFinanceInvoicePaymentApiSuccess,
  recordFinanceInvoicePaymentsSequentially,
} from "../../../utilis/financeInvoicePaymentHelpers";
import {
  getInvoiceStatusLabel,
  normalizeInvoiceStatus,
} from "../../../utilis/invoiceStatusHelpers";
import { buildCommissionDetailInvoicesReturnUrl } from "./commissionDetailUtils";
import {
  fetchCommissionInvoice,
  updateCommissionInvoice,
  voidCommissionInvoice,
  moveCommissionInvoiceToDraft,
} from "./commissionsApi";
import { canAccessCommissionInvoiceEditor } from "../../../utilis/invoicePermissionHelpers";
import {
  buildFinanceInvoicesReturnUrl,
  isFinanceInvoicesHubReturnPath,
  mapFinanceInvoicePayloadTaxTypesForApi,
  readFinanceInvoiceListTabsFromLocation,
} from "../../../utilis/invoiceCreateHelpers";
import { applyCommissionInvoiceLineTotals } from "./commissionInvoiceLineSuggestions";
import {
  applyCommissionApproveFields,
  sumCommissionPayloadPaidAmount,
} from "./commissionInvoiceApproveHelpers";
import {
  enrichLineItemsWithLinkedInstallments,
  mapCommissionInvoiceToWizardRecord,
} from "./commissionInvoiceRecordMapper";
import {
  countCommissionInvoicePayments,
  mapCommissionPaymentsFromPayload,
  resolveCommissionPaymentTargetForIndex,
} from "./commissionInvoicePaymentHelpers";
import {
  parseCommissionDetailIdsFromUrl,
  pickClientDisplayName,
  pickClientDob,
  pickClientStudentId,
  suggestionMetaFromViewModel,
} from "./commissionInvoiceSuggestionMeta";
import {
  buildCommissionInvoicePlanMeta,
  normalizeSavedCommissionPlan,
} from "./detail/commissionPlanUtils";
import { mapPlanPayloadInstallments } from "./detail/commissionInstallmentUtils";
import { buildProviderDisplayProfileFromApi } from "../../../utilis/applicationCostHelpers";
import { mergeProviderBillToForInvoice } from "./commissionBillToUtils";
import { mapClientSnapshotToProfile } from "../../../utilis/invoiceEditHelpers";
import { showCommissionInvoiceSuccessToast } from "./commissionInvoiceToast";
import { CONTEXT_INVOICE_COMMISSION } from "../../../utilis/followupConstants";
import {
  applyInvoiceCatalog,
  fetchCommissionInvoiceDetail,
  fetchInvoiceNoteList,
  mapWorkspacePlanInvoiceIndex,
  planPayloadFromWorkspace,
  suggestionViewModelFromWorkspaceApplication,
  workspaceErrorMessage,
  wrapCommissionWorkspaceInvoice,
} from "../../../utilis/invoiceWorkspaceApi";
import "../../Clients/clients.css";

function isFinanceInvoicesReturnTo(returnTo) {
  return isFinanceInvoicesHubReturnPath(returnTo);
}

function collectInstallmentIdsFromLines(lineItems = []) {
  const commission = [];
  const bonus = [];
  (Array.isArray(lineItems) ? lineItems : []).forEach((line) => {
    if (line?.commission_installment_id) {
      commission.push(Number(line.commission_installment_id));
    }
    if (line?.bonus_installment_id) {
      bonus.push(Number(line.bonus_installment_id));
    }
  });
  return {
    commission_installment_ids: [...new Set(commission.filter(Boolean))],
    bonus_installment_ids: [...new Set(bonus.filter(Boolean))],
  };
}

function payloadHasBillableInvoiceLines(payload) {
  const lines = Array.isArray(payload?.line_items) ? payload.line_items : [];
  return lines.some((line) => {
    const feeType = String(line?.fee_type || "").toLowerCase();
    const hasDesc = String(line?.description || "").trim();
    const hasAmount =
      (Number(line?.unit_price) || 0) > 0 && (Number(line?.quantity) || 0) > 0;
    if (
      (feeType === "application_fee" || feeType === "material_fee") &&
      hasAmount
    ) {
      return true;
    }
    const linked =
      line?.commission_installment_id || line?.bonus_installment_id;
    return hasDesc && hasAmount && !linked;
  });
}

function CommissionEditInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId: clientIdParam, invoiceId } = useParams();
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [invoiceSetting, setInvoiceSetting] = useState(null);
  const [discountFeeRules, setDiscountFeeRules] = useState([]);
  const [existingInvoice, setExistingInvoice] = useState(null);
  const [invoiceNotes, setInvoiceNotes] = useState([]);
  const [loadingInvoiceNotes, setLoadingInvoiceNotes] = useState(false);
  const [addingInvoiceNote, setAddingInvoiceNote] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingFinancePayment, setSavingFinancePayment] = useState(false);
  const [reversingFinancePayment, setReversingFinancePayment] = useState(false);
  const [voidingInvoice, setVoidingInvoice] = useState(false);
  const [movingInvoiceToDraft, setMovingInvoiceToDraft] = useState(false);
  const [commissionInstallments, setCommissionInstallments] = useState([]);
  const [bonusInstallments, setBonusInstallments] = useState([]);
  const [commissionPlan, setCommissionPlan] = useState(null);
  const [planCommissionInvoices, setPlanCommissionInvoices] = useState([]);
  const [suggestionMeta, setSuggestionMeta] = useState({
    currency: "AUD",
    courseName: "",
    providerName: "",
    studentName: "",
    studentId: "",
    studentDob: "",
    startDate: "",
    endDate: "",
    commissionRate: "",
    applicationId: null,
    applicationType: "education",
    providerId: "",
  });
  const [providerBillTo, setProviderBillTo] = useState(null);
  const [followupUsers, setFollowupUsers] = useState([]);
  const [followupRefreshKey, setFollowupRefreshKey] = useState(0);
  const [followupCounts, setFollowupCounts] = useState({ pending: 0, total: 0 });
  const [invoiceCatalog, setInvoiceCatalog] = useState(null);

  const clientId = useMemo(
    () => String(clientIdParam || "").trim(),
    [clientIdParam],
  );
  const resolvedInvoiceId = useMemo(
    () => String(invoiceId || "").trim(),
    [invoiceId],
  );

  const billToProfile = useMemo(() => {
    if (providerBillTo?.fullName) return providerBillTo;
    return buildProviderDisplayProfileFromApi({
      name: suggestionMeta.providerName || "",
    });
  }, [providerBillTo, suggestionMeta.providerName]);

  const installmentSuggestionSource = useMemo(
    () => ({
      commissionInstallments,
      bonusInstallments,
      applicationId:
        suggestionMeta.applicationId || existingInvoice?.application_id,
      applicationType:
        suggestionMeta.applicationType ||
        existingInvoice?.application_type ||
        "education",
      currency: suggestionMeta.currency || existingInvoice?.currency || "AUD",
      courseName: suggestionMeta.courseName,
      providerName: suggestionMeta.providerName,
      studentName: suggestionMeta.studentName,
      studentId: suggestionMeta.studentId,
      studentDob: suggestionMeta.studentDob,
      startDate: suggestionMeta.startDate,
      endDate: suggestionMeta.endDate,
      defaultCommissionRate: suggestionMeta.commissionRate,
      commissionType: suggestionMeta.commissionType || "percentage",
      includeInquiryFees: false,
      commissionInvoices: planCommissionInvoices,
      excludeInvoiceId: resolvedInvoiceId
        ? Number(resolvedInvoiceId) || null
        : null,
      currentInvoiceId: resolvedInvoiceId
        ? Number(resolvedInvoiceId) || null
        : null,
    }),
    [
      commissionInstallments,
      bonusInstallments,
      suggestionMeta,
      existingInvoice,
      resolvedInvoiceId,
      planCommissionInvoices,
    ],
  );

  const commissionPlanMeta = useMemo(
    () => buildCommissionInvoicePlanMeta(commissionPlan),
    [commissionPlan],
  );

  const returnTo = useMemo(() => {
    const fromState = location.state?.returnTo;
    if (fromState) return fromState;
    const params = new URLSearchParams(location.search || "");
    const fromQuery = params.get("returnTo");
    if (fromQuery) return fromQuery;
    const tabs = readFinanceInvoiceListTabsFromLocation(location);
    if (params.get("from") === "invoices" || tabs.moduleTab === "commission") {
      return buildFinanceInvoicesReturnUrl({
        ...tabs,
        moduleTab: "commission",
      });
    }
    return "/commissions-invoices";
  }, [location]);

  const commissionShellStatus = useMemo(() => {
    if (existingInvoice?.status) {
      const normalized = normalizeInvoiceStatus(existingInvoice.status);
      if (normalized === "draft" || normalized === "awaiting_approval") {
        return normalized;
      }
    }
    if (existingInvoice?.approved) return "approved";
    if (existingInvoice?.status) {
      return normalizeInvoiceStatus(existingInvoice.status);
    }
    const fromState = location.state?.invoiceStatus;
    if (fromState) return normalizeInvoiceStatus(fromState);
    return "draft";
  }, [
    location.state?.invoiceStatus,
    existingInvoice?.approved,
    existingInvoice?.status,
  ]);

  const isDraftLikeCommissionInvoice = useMemo(
    () =>
      commissionShellStatus === "draft" ||
      commissionShellStatus === "awaiting_approval",
    [commissionShellStatus],
  );

  const planId = existingInvoice?._planId || commissionPlan?.id || null;

  const invoiceFollowupConfig = useMemo(() => {
    if (!resolvedInvoiceId) return null;
    const providerId =
      Number(
        suggestionMeta.providerId ||
          existingInvoice?.provider_id ||
          existingInvoice?.providerId ||
          "",
      ) || null;
    const applicationId =
      Number(
        suggestionMeta.applicationId || existingInvoice?.application_id || "",
      ) || null;
    return {
      financeInvoiceId: Number(resolvedInvoiceId),
      contextType: CONTEXT_INVOICE_COMMISSION,
      commissionPlanId: planId ? Number(planId) : null,
      clientId: clientId ? Number(clientId) : null,
      providerId,
      applicationId,
      applicationCourseId:
        Number(existingInvoice?.application_course_id || "") || null,
      users: followupUsers,
      pendingCount: followupCounts.pending,
      totalCount: followupCounts.total,
      refreshKey: followupRefreshKey,
      onFollowupAdded: () => setFollowupRefreshKey((k) => k + 1),
    };
  }, [
    resolvedInvoiceId,
    planId,
    clientId,
    suggestionMeta.providerId,
    suggestionMeta.applicationId,
    existingInvoice,
    followupUsers,
    followupCounts,
    followupRefreshKey,
  ]);

  const handleSaveComplete = useCallback(() => {
    navigate(buildCommissionDetailInvoicesReturnUrl(returnTo, { planId }));
  }, [navigate, returnTo, planId]);

  const handleCatalogLoaded = useCallback((catalog) => {
    const mapped = applyInvoiceCatalog(catalog || {});
    setInvoiceCatalog(catalog || null);
    if (mapped.setting) setInvoiceSetting(mapped.setting);
    setDiscountFeeRules(mapped.discountRules);
    setFollowupUsers(mapped.followupUsers);
  }, []);

  const handleAfterSaveAndAnother = useCallback(async () => {
    if (!resolvedInvoiceId) return;
    try {
      const full = await fetchCommissionInvoice(resolvedInvoiceId);
      setExistingInvoice(mapCommissionInvoiceToWizardRecord(full));
    } catch (error) {
      console.error("Failed to refresh draft commission invoice", error);
    }
  }, [resolvedInvoiceId]);

  const fetchInvoiceNotes = async (
    targetInvoiceId = resolvedInvoiceId,
    { silent = false } = {},
  ) => {
    if (!targetInvoiceId) {
      setInvoiceNotes([]);
      return [];
    }

    try {
      if (!silent) setLoadingInvoiceNotes(true);
      const notes = normalizeInvoiceNotesList({
        data: {
          success: true,
          data: await fetchInvoiceNoteList(targetInvoiceId, {
            module: "commission",
          }),
        },
      });
      setInvoiceNotes(notes);
      return notes;
    } catch (error) {
      console.error("Failed to load invoice notes", error);
      return [];
    } finally {
      if (!silent) setLoadingInvoiceNotes(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadData = async () => {
      if (!clientId || !resolvedInvoiceId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const workspace = await fetchCommissionInvoiceDetail({
          invoiceId: resolvedInvoiceId,
          signal: controller.signal,
        });
        if (cancelled) return;

        setFollowupCounts(
          workspace.followup_counts || { pending: 0, total: 0 },
        );

        const clientRecord = workspace.client || null;
        setClientData(clientRecord);

        const clientName = pickClientDisplayName(clientRecord);
        const clientDob = pickClientDob(clientRecord);
        const clientStudentId = pickClientStudentId(clientRecord);

        const invoicePayload = wrapCommissionWorkspaceInvoice(workspace.invoice);
        const mapped = mapCommissionInvoiceToWizardRecord(invoicePayload);
        const urlIds = parseCommissionDetailIdsFromUrl(
          location.state?.returnTo || location.search || "",
        );
        const stateCommissionIds = (
          location.state?.commissionInstallmentIds || []
        )
          .map(Number)
          .filter(Boolean);
        const stateBonusIds = (location.state?.bonusInstallmentIds || [])
          .map(Number)
          .filter(Boolean);

        if (
          mapped &&
          !(mapped._commissionInstallmentIds || []).length &&
          stateCommissionIds.length
        ) {
          mapped._commissionInstallmentIds = stateCommissionIds;
        }
        if (
          mapped &&
          !(mapped._bonusInstallmentIds || []).length &&
          stateBonusIds.length
        ) {
          mapped._bonusInstallmentIds = stateBonusIds;
        }

        const planRows = mapWorkspacePlanInvoiceIndex(
          workspace.plan_invoices_index,
        );
        setPlanCommissionInvoices(planRows);

        const planPayload = planPayloadFromWorkspace(workspace);
        const normalizedPlan = planPayload.plan
          ? normalizeSavedCommissionPlan(planPayload.plan)
          : null;
        if (normalizedPlan) {
          setCommissionPlan(normalizedPlan);
        }

        const {
          commissionInstallments: loadedCommissionInstallments,
          bonusInstallments: loadedBonusInstallments,
        } = mapPlanPayloadInstallments(planPayload);
        setCommissionInstallments(loadedCommissionInstallments);
        setBonusInstallments(loadedBonusInstallments);

        setExistingInvoice(
          mapped
            ? applyCommissionInvoiceLineTotals(
                {
                  ...mapped,
                  _planId: normalizedPlan?.id || mapped._planId || null,
                },
                loadedCommissionInstallments,
                loadedBonusInstallments,
                {
                  defaultCommissionRate:
                    location.state?.commissionRate ||
                    normalizedPlan?.commissionRate ||
                    "",
                },
              )
            : mapped,
        );

        const snapshotBillTo = mapClientSnapshotToProfile(
          mapped?.client_snapshot,
        );
        const workspaceBillTo = workspace.provider_bill_to?.detail
          ? buildProviderDisplayProfileFromApi(
              workspace.provider_bill_to.detail,
              Array.isArray(workspace.provider_bill_to.persondetail)
                ? workspace.provider_bill_to.persondetail
                : [],
            )
          : null;
        setProviderBillTo(
          mergeProviderBillToForInvoice(
            snapshotBillTo ||
              buildProviderDisplayProfileFromApi({
                name:
                  mapped?.client_name ||
                  mapped?.clientName ||
                  location.state?.providerName ||
                  "",
              }),
            workspaceBillTo,
          ),
        );

        const applicationId =
          mapped?.application_id ||
          location.state?.applicationId ||
          urlIds.applicationId ||
          normalizedPlan?.applicationId ||
          workspace.application?.application_id ||
          null;
        const applicationType =
          mapped?.application_type ||
          location.state?.applicationType ||
          urlIds.applicationType ||
          "education";

        let detailMeta = suggestionMetaFromViewModel(
          suggestionViewModelFromWorkspaceApplication(workspace.application),
          {
            courseName: location.state?.courseName || "",
            providerName: location.state?.providerName || "",
            studentName: location.state?.studentName || clientName || "",
            studentId: location.state?.studentId || clientStudentId || "",
            studentDob: location.state?.studentDob || clientDob || "",
            startDate: location.state?.startDate || "",
            endDate: location.state?.endDate || "",
            commissionRate:
              location.state?.commissionRate ||
              normalizedPlan?.commissionRate ||
              workspace.application?.commission_rate ||
              "",
            commissionType:
              location.state?.commissionType ||
              normalizedPlan?.commissionType ||
              "percentage",
            providerId:
              location.state?.providerId ||
              urlIds.providerId ||
              normalizedPlan?.providerId ||
              workspace.application?.provider_id ||
              "",
            courseId:
              location.state?.courseId ||
              urlIds.courseId ||
              normalizedPlan?.courseId ||
              workspace.application?.course_id ||
              "",
            applicationId,
            applicationType,
          },
        );
        detailMeta.studentName = detailMeta.studentName || clientName || "";
        detailMeta.studentId = detailMeta.studentId || clientStudentId || "";
        detailMeta.studentDob = detailMeta.studentDob || clientDob || "";

        setSuggestionMeta({
          currency:
            normalizedPlan?.currency ||
            workspace.application?.currency ||
            mapped?.currency ||
            "AUD",
          courseName: detailMeta.courseName || "",
          providerName: detailMeta.providerName || "",
          studentName: detailMeta.studentName || clientName || "",
          studentId: detailMeta.studentId || clientStudentId || "",
          studentDob: detailMeta.studentDob || clientDob || "",
          startDate: detailMeta.startDate || "",
          endDate: detailMeta.endDate || "",
          commissionRate:
            detailMeta.commissionRate || normalizedPlan?.commissionRate || "",
          commissionType:
            detailMeta.commissionType ||
            normalizedPlan?.commissionType ||
            "percentage",
          applicationId:
            applicationId || normalizedPlan?.applicationId || null,
          applicationType,
          providerId: String(
            normalizedPlan?.providerId ||
              detailMeta.providerId ||
              mapped?.provider_id ||
              mapped?.providerId ||
              "",
          ).trim(),
        });

        if (
          mapped &&
          !(mapped._commissionInstallmentIds || []).length &&
          !(mapped._bonusInstallmentIds || []).length
        ) {
          const match = planRows.find(
            (row) =>
              String(row?.id) === String(mapped.id) ||
              String(row?.financeInvoiceId) === String(mapped.id),
          );
          if (match) {
            const recovered = {
              commission_installment_ids: (match.commissionInstallmentIds || [])
                .map(Number)
                .filter(Boolean),
              bonus_installment_ids: (match.bonusInstallmentIds || [])
                .map(Number)
                .filter(Boolean),
            };
            setExistingInvoice((prev) =>
              prev
                ? applyCommissionInvoiceLineTotals(
                    {
                      ...prev,
                      _commissionInstallmentIds:
                        recovered.commission_installment_ids,
                      _bonusInstallmentIds: recovered.bonus_installment_ids,
                      line_items: enrichLineItemsWithLinkedInstallments(
                        prev.line_items || [],
                        recovered,
                      ),
                    },
                    loadedCommissionInstallments,
                    loadedBonusInstallments,
                    {
                      defaultCommissionRate:
                        detailMeta.commissionRate ||
                        location.state?.commissionRate ||
                        "",
                    },
                  )
                : prev,
            );
          }
        }

        setInvoiceNotes([]);

        if (!clientRecord || !mapped) {
          Swal.fire("Error", "Failed to load commission invoice.", "error");
        }
      } catch (error) {
        if (cancelled) return;
        const detail = workspaceErrorMessage(
          error,
          "Failed to load commission invoice.",
        );
        if (!detail) return;
        console.error(error);
        Swal.fire("Error", detail, "error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, resolvedInvoiceId, location.state]);

  const handleClose = () => {
    navigate(returnTo);
  };

  const refreshExistingInvoiceRecord = useCallback(
    async (targetInvoiceId = resolvedInvoiceId) => {
      if (!targetInvoiceId) return null;
      try {
        const invoicePayload = await fetchCommissionInvoice(targetInvoiceId);
        const mapped = mapCommissionInvoiceToWizardRecord(invoicePayload);
        if (mapped) {
          const refreshed = applyCommissionInvoiceLineTotals(
            ensureFinanceInvoiceApprovedState({
              ...mapped,
              _commissionInstallmentIds: mapped._commissionInstallmentIds
                ?.length
                ? mapped._commissionInstallmentIds
                : existingInvoice?._commissionInstallmentIds || [],
              _bonusInstallmentIds: mapped._bonusInstallmentIds?.length
                ? mapped._bonusInstallmentIds
                : existingInvoice?._bonusInstallmentIds || [],
              _planId: mapped._planId || existingInvoice?._planId || null,
            }),
            commissionInstallments,
            bonusInstallments,
            { defaultCommissionRate: suggestionMeta.commissionRate || "" },
          );
          setExistingInvoice(refreshed);
          return refreshed;
        }
      } catch (error) {
        console.error("Failed to refresh commission invoice", error);
      }
      return null;
    },
    [
      resolvedInvoiceId,
      existingInvoice,
      commissionInstallments,
      bonusInstallments,
      suggestionMeta.commissionRate,
    ],
  );

  const refreshInvoiceAfterPaymentMutation = async (targetInvoiceId) => {
    await refreshExistingInvoiceRecord(targetInvoiceId);
    await fetchInvoiceNotes(targetInvoiceId, { silent: true });
  };

  const handleVoidInvoice = async () => {
    const targetInvoiceId = existingInvoice?.id || resolvedInvoiceId;
    if (!targetInvoiceId) return false;

    const invoiceNumber =
      existingInvoice?.invoice_number ||
      existingInvoice?.invoice_no ||
      targetInvoiceId;

    const confirmed = await Swal.fire({
      title: "Void invoice?",
      text: `Move invoice ${invoiceNumber} to Void? The record will be kept for reference.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Void",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#64748b",
    });

    if (!confirmed.isConfirmed) return false;

    try {
      setVoidingInvoice(true);
      await voidCommissionInvoice(targetInvoiceId);
      await Swal.fire({
        icon: "success",
        title: "Voided",
        text: "Invoice moved to Void.",
        timer: 1400,
        showConfirmButton: false,
      });
      navigate(
        buildCommissionDetailInvoicesReturnUrl(returnTo, {
          planId,
          invoiceStatusTab: "void",
        }),
      );
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to void commission invoice.",
        "error",
      );
      return false;
    } finally {
      setVoidingInvoice(false);
    }
  };

  const handleMoveInvoiceToDraft = async () => {
    const targetInvoiceId = existingInvoice?.id || resolvedInvoiceId;
    if (!targetInvoiceId) return false;

    const invoiceNumber =
      existingInvoice?.invoice_number ||
      existingInvoice?.invoice_no ||
      targetInvoiceId;

    const confirmed = await Swal.fire({
      title: "Move to draft?",
      text: `Invoice ${invoiceNumber} will return to Draft so you can edit and approve again.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Move to draft",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#64748b",
    });

    if (!confirmed.isConfirmed) return false;

    try {
      setMovingInvoiceToDraft(true);
      await moveCommissionInvoiceToDraft(targetInvoiceId);
      await Swal.fire({
        icon: "success",
        title: "Moved to draft",
        text: "Invoice is now in Draft.",
        timer: 1400,
        showConfirmButton: false,
      });
      if (isFinanceInvoicesReturnTo(returnTo)) {
        const tabs = readFinanceInvoiceListTabsFromLocation(location);
        navigate(
          buildFinanceInvoicesReturnUrl({
            statusTab: "draft",
            moduleTab: tabs.moduleTab,
          }),
        );
      } else {
        navigate(
          buildCommissionDetailInvoicesReturnUrl(returnTo, {
            planId: planId || location.state?.planId,
            invoiceStatusTab: "draft",
          }),
        );
      }
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Failed to move commission invoice to draft.",
        "error",
      );
      return false;
    } finally {
      setMovingInvoiceToDraft(false);
    }
  };

  const handleAddFinanceInvoicePayment = async (
    payment,
    receiptFile = null,
    receiptFiles = null,
    options = {},
  ) => {
    const {
      silent = false,
      skipNavigate = false,
      skipRefresh = false,
      skipSavingState = false,
    } = options;
    const targetInvoiceId = existingInvoice?.id || resolvedInvoiceId;
    if (!targetInvoiceId) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }

    const requestedAmount = Math.abs(Number(payment?.amount) || 0);
    if (requestedAmount <= 0) {
      Swal.fire(
        "Invalid amount",
        "Enter a payment amount greater than 0.",
        "warning",
      );
      return false;
    }

    const syncedInvoice = applyCommissionInvoiceLineTotals(
      existingInvoice ||
        normalizeFinanceInvoiceFromRecord({ id: targetInvoiceId }),
      commissionInstallments,
      bonusInstallments,
      { defaultCommissionRate: suggestionMeta.commissionRate || "" },
    );
    const invoiceTotal = Math.max(Number(syncedInvoice?.total_amount) || 0, 0);
    if (invoiceTotal <= 0) {
      Swal.fire("Error", "Invoice total is missing.", "error");
      return false;
    }

    const transactions = resolveFinanceInvoiceTransactions(syncedInvoice);
    const reversiblePayments = transactions.filter((record) =>
      canReverseInvoiceLedgerTransaction(record, transactions),
    );
    const paidAmount = Math.max(resolveInvoicePaidAmount(syncedInvoice), 0);
    const remainingDue = Math.max(invoiceTotal - paidAmount, 0);

    let payAmount = requestedAmount;
    let shouldAutoReverse = false;

    if (requestedAmount > remainingDue + 0.0001) {
      if (!reversiblePayments.length) {
        Swal.fire(
          "Invalid amount",
          `Payment cannot exceed the remaining balance (${invoiceTotal.toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          )} total).`,
          "warning",
        );
        return false;
      }

      const confirm = await Swal.fire({
        title: "Reverse existing payment?",
        html: `This invoice total is <strong>${invoiceTotal.toLocaleString(
          undefined,
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )}</strong>. Existing payment(s) will be reversed first, then your new payment will be recorded (never more than the invoice total).`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Reverse & pay",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return false;

      shouldAutoReverse = true;
      payAmount = Math.min(requestedAmount, invoiceTotal);
    } else {
      payAmount = Math.min(requestedAmount, remainingDue);
    }

    if (payAmount <= 0) {
      Swal.fire(
        "Invalid amount",
        "Nothing left to pay on this invoice.",
        "warning",
      );
      return false;
    }

    try {
      if (!skipSavingState) {
        setSavingFinancePayment(true);
      }

      if (shouldAutoReverse) {
        setReversingFinancePayment(true);
        for (const record of reversiblePayments) {
          const reverseResponse = await apiUrl.post(
            `reverseinvoicepayment/${targetInvoiceId}`,
            buildFinanceInvoiceReversePaymentPayload({
              transactionId: record.id,
              amount: record.amount,
              reason: "Auto-reversed before recording a replacement payment",
            }),
          );
          if (!isFinanceInvoicePaymentApiSuccess(reverseResponse)) {
            Swal.fire(
              "Error",
              getFinanceInvoicePaymentApiMessage(
                reverseResponse,
                "Failed to reverse existing payment.",
              ),
              "error",
            );
            return false;
          }
          setExistingInvoice((prev) =>
            applyFinanceInvoiceMutationFromResponse(
              prev ||
                normalizeFinanceInvoiceFromRecord({ id: targetInvoiceId }),
              reverseResponse,
            ),
          );
        }
        setReversingFinancePayment(false);
      }

      const lineItems =
        syncedInvoice?.line_items || existingInvoice?.line_items || [];
      const paymentTarget = payment?.line_number
        ? {}
        : resolveCommissionPaymentTargetForIndex(
            lineItems,
            countCommissionInvoicePayments(transactions),
          );

      const formData = buildFinanceInvoicePaymentFormData({
        invoiceId: targetInvoiceId,
        clientId,
        payment: {
          ...paymentTarget,
          ...payment,
          amount: payAmount,
        },
        receiptFile,
        receiptFiles,
      });

      const response = await apiUrl.post(
        `invoicepayment/${targetInvoiceId}`,
        formData,
        buildMultipartFormDataRequestConfig(),
      );

      if (!isFinanceInvoicePaymentApiSuccess(response)) {
        Swal.fire(
          "Error",
          getFinanceInvoicePaymentApiMessage(
            response,
            "Failed to record payment.",
          ),
          "error",
        );
        return false;
      }

      setExistingInvoice((prev) =>
        applyCommissionInvoiceLineTotals(
          applyFinanceInvoiceMutationFromResponse(
            prev || normalizeFinanceInvoiceFromRecord({ id: targetInvoiceId }),
            response,
          ),
          commissionInstallments,
          bonusInstallments,
          { defaultCommissionRate: suggestionMeta.commissionRate || "" },
        ),
      );
      setInvoiceNotes((prev) =>
        applyInvoiceNotesMutationFromResponse(prev, response),
      );

      if (skipRefresh) {
        return true;
      }

      await refreshInvoiceAfterPaymentMutation(targetInvoiceId);

      let refreshedStatus = normalizeInvoiceStatus(
        existingInvoice?.status || "awaiting_payment",
      );
      try {
        const full = await fetchCommissionInvoice(targetInvoiceId);
        const mapped = mapCommissionInvoiceToWizardRecord(full);
        if (mapped) {
          setExistingInvoice(
            applyCommissionInvoiceLineTotals(
              {
                ...mapped,
                approved: Boolean(
                  mapped?.approved ?? existingInvoice?.approved,
                ),
              },
              commissionInstallments,
              bonusInstallments,
              { defaultCommissionRate: suggestionMeta.commissionRate || "" },
            ),
          );
          refreshedStatus = normalizeInvoiceStatus(mapped.status);
        }
      } catch (refreshError) {
        const payload = response?.data?.data ?? response?.data ?? {};
        const invoicePayload = payload.invoice ?? payload;
        refreshedStatus = normalizeInvoiceStatus(
          invoicePayload?.status || invoicePayload?.payment_status,
        );
      }

      const paymentReturnUrl = buildCommissionDetailInvoicesReturnUrl(
        returnTo,
        {
          planId: existingInvoice?._planId || planId,
        },
      );

      if (refreshedStatus === "paid") {
        showCommissionInvoiceSuccessToast(
          getFinanceInvoicePaymentApiMessage(response, "Invoice paid in full."),
        );
      } else if (!silent) {
        showCommissionInvoiceSuccessToast(
          getFinanceInvoicePaymentApiMessage(
            response,
            shouldAutoReverse
              ? "Previous payment reversed and new payment recorded."
              : refreshedStatus === "partial"
                ? "Partial payment recorded."
                : "Payment recorded successfully.",
          ),
        );
      }

      if (!skipNavigate) {
        navigate(paymentReturnUrl);
      }
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to record payment.",
        "error",
      );
      return false;
    } finally {
      if (!skipSavingState) {
        setSavingFinancePayment(false);
        setReversingFinancePayment(false);
      }
    }
  };

  const handleAddFinanceInvoiceBulkPayment = async (entries) => {
    const targetInvoiceId = existingInvoice?.id || resolvedInvoiceId;
    const paymentReturnUrl = buildCommissionDetailInvoicesReturnUrl(returnTo, {
      planId: existingInvoice?._planId || planId,
    });

    try {
      setSavingFinancePayment(true);
      const saved = await recordFinanceInvoicePaymentsSequentially(
        entries,
        (payment, receiptFile, receiptFiles, options = {}) =>
          handleAddFinanceInvoicePayment(payment, receiptFile, receiptFiles, {
            ...options,
            skipNavigate: true,
            skipRefresh: true,
            skipSavingState: true,
          }),
      );

      if (saved && targetInvoiceId) {
        await refreshExistingInvoiceRecord(targetInvoiceId);
        await fetchInvoiceNotes(targetInvoiceId, { silent: true });
        showCommissionInvoiceSuccessToast("Payment recorded successfully.");
        navigate(paymentReturnUrl);
      }

      return saved;
    } finally {
      setSavingFinancePayment(false);
      setReversingFinancePayment(false);
    }
  };

  const handleReverseFinanceInvoicePayment = async (payload) => {
    const targetInvoiceId = existingInvoice?.id || resolvedInvoiceId;
    if (!targetInvoiceId || !payload?.transaction_id) {
      Swal.fire("Error", "Payment details are missing.", "error");
      return false;
    }

    try {
      setReversingFinancePayment(true);
      const response = await apiUrl.post(
        `reverseinvoicepayment/${targetInvoiceId}`,
        buildFinanceInvoiceReversePaymentPayload({
          transactionId: payload.transaction_id,
          amount: payload.amount,
          reason: payload.reason,
        }),
      );

      if (!isFinanceInvoicePaymentApiSuccess(response)) {
        Swal.fire(
          "Error",
          getFinanceInvoicePaymentApiMessage(
            response,
            "Failed to reverse payment.",
          ),
          "error",
        );
        return false;
      }

      setExistingInvoice((prev) =>
        applyFinanceInvoiceMutationFromResponse(
          prev || normalizeFinanceInvoiceFromRecord({ id: targetInvoiceId }),
          response,
        ),
      );
      setInvoiceNotes((prev) =>
        applyInvoiceNotesMutationFromResponse(prev, response),
      );
      await refreshInvoiceAfterPaymentMutation(targetInvoiceId);
      showCommissionInvoiceSuccessToast(
        getFinanceInvoicePaymentApiMessage(
          response,
          "Payment reversed successfully.",
        ),
      );
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to reverse payment.",
        "error",
      );
      return false;
    } finally {
      setReversingFinancePayment(false);
    }
  };

  const handleAddInvoiceNote = async (noteText) => {
    const details = String(noteText || "").trim();
    if (!details) {
      Swal.fire("Warning", "Please enter a note.", "warning");
      return false;
    }

    const invoiceRecord = existingInvoice;
    const targetInvoiceId = invoiceRecord?.id || resolvedInvoiceId;
    const invoiceNumber =
      invoiceRecord?.invoice_number ||
      invoiceRecord?.invoice_no ||
      invoiceRecord?.invoiceNo;

    if (!targetInvoiceId || !invoiceNumber) {
      Swal.fire("Error", "Invoice details are missing.", "error");
      return false;
    }

    try {
      setAddingInvoiceNote(true);
      const response = await apiUrl.post("addnoteforinvoice", {
        invoice_id: targetInvoiceId,
        invoice_number: invoiceNumber,
        details,
      });

      if (response?.data?.success || response?.data?.status) {
        const refreshed = await fetchInvoiceNotes(targetInvoiceId);
        if (!refreshed.length) {
          // Optimistic append if notes endpoint is empty but add succeeded.
          setInvoiceNotes((prev) => [
            {
              id: `local-${Date.now()}`,
              invoice_id: targetInvoiceId,
              action: "note",
              details,
              action_date: new Date().toISOString(),
              created_at: new Date().toISOString(),
              user_name: "You",
            },
            ...(prev || []),
          ]);
        }
        return true;
      }

      Swal.fire(
        "Error",
        response?.data?.error ||
          response?.data?.message ||
          "Failed to add note.",
        "error",
      );
      return false;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to add note.",
        "error",
      );
      return false;
    } finally {
      setAddingInvoiceNote(false);
    }
  };

  const handleUpdate = async ({ id, payload }, options = {}) => {
    const targetId = id || resolvedInvoiceId;
    const isApprove = Boolean(options?.approve);
    if (!targetId) {
      Swal.fire("Error", "Invoice could not be updated.", "error");
      return false;
    }

    setSaving(true);
    try {
      const payments = mapCommissionPaymentsFromPayload(
        payload,
        payload?.line_items || [],
      );
      const fromLines = collectInstallmentIdsFromLines(payload?.line_items);
      const lines = Array.isArray(payload?.line_items)
        ? payload.line_items
        : [];
      const allLinesLinked =
        lines.length > 0 &&
        lines.every(
          (line) =>
            line?.commission_installment_id || line?.bonus_installment_id,
        );

      let commissionIds;
      let bonusIds;
      if (allLinesLinked) {
        commissionIds = fromLines.commission_installment_ids;
        bonusIds = fromLines.bonus_installment_ids;
      } else if (
        fromLines.commission_installment_ids.length ||
        fromLines.bonus_installment_ids.length
      ) {
        commissionIds = [
          ...new Set([
            ...fromLines.commission_installment_ids,
            ...(existingInvoice?._commissionInstallmentIds || []),
          ]),
        ];
        bonusIds = [
          ...new Set([
            ...fromLines.bonus_installment_ids,
            ...(existingInvoice?._bonusInstallmentIds || []),
          ]),
        ];
      } else {
        commissionIds = existingInvoice?._commissionInstallmentIds || [];
        bonusIds = existingInvoice?._bonusInstallmentIds || [];
      }

      if (
        !commissionIds.length &&
        !bonusIds.length &&
        !payloadHasBillableInvoiceLines(payload)
      ) {
        Swal.fire(
          "Missing line items",
          "Add an installment, application fee, material fee, or another line with description and amount.",
          "warning",
        );
        return false;
      }

      let requestBody = {
        ...payload,
        invoice_date: payload?.issue_date || payload?.invoice_date || null,
        due_date: payload?.due_date || null,
        remarks: payload?.reference || payload?.remarks || null,
        status: payload?.status || "draft",
        currency: payload?.currency || "AUD",
        branding_id: payload?.branding_id || null,
        branch_id: payload?.branch_id || null,
        line_items: payload?.line_items || [],
        commission_installment_ids: commissionIds,
        bonus_installment_ids: bonusIds,
      };
      if (payments.length > 0) {
        requestBody.payments = payments;
        if (payments.length === 1) {
          requestBody.payment = payments[0];
        }
      }

      if (isApprove) {
        requestBody = applyCommissionApproveFields(requestBody, {
          totalAmount: payload?.total_amount,
          paidAmount: sumCommissionPayloadPaidAmount(payload, payments),
        });
      }

      await updateCommissionInvoice(
        targetId,
        mapFinanceInvoicePayloadTaxTypesForApi(requestBody),
      );

      const currentStatus = normalizeInvoiceStatus(existingInvoice?.status);
      const requestedStatus = normalizeInvoiceStatus(
        requestBody.status || payload?.status || "draft",
      );
      const isDraftLike =
        currentStatus === "draft" || currentStatus === "awaiting_approval";
      const isApproveAction = isDraftLike && isApprove;
      const isAwaitingPaymentUpdate =
        currentStatus === "awaiting_payment" &&
        requestedStatus === "awaiting_payment";

      if (isDraftLike) {
        const saveAndAnother = Boolean(options?.saveAndAnother);

        showCommissionInvoiceSuccessToast(
          isApproveAction
            ? "Invoice approved."
            : `Commission invoice saved as ${getInvoiceStatusLabel(requestedStatus)}.`,
        );

        if (saveAndAnother) {
          await handleAfterSaveAndAnother();
          return { success: true, saveAndAnother: true, stayOnPage: true };
        }

        if (isApproveAction) {
          await refreshExistingInvoiceRecord(targetId);
          return { success: true, stayOnPage: true };
        }

        await refreshExistingInvoiceRecord(targetId);
        return { success: true, stayOnPage: false };
      }

      if (isAwaitingPaymentUpdate) {
        showCommissionInvoiceSuccessToast("Invoice updated.");
        await refreshExistingInvoiceRecord(targetId);
        return { success: true, stayOnPage: true };
      }

      const statusLabel = getInvoiceStatusLabel(payload?.status);
      showCommissionInvoiceSuccessToast(
        `Commission invoice updated as ${statusLabel}.`,
      );
      navigate(returnTo);
      return true;
    } catch (error) {
      Swal.fire(
        "Error",
        error?.message || "Unable to update commission invoice.",
        "error",
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  if (!canAccessCommissionInvoiceEditor()) {
    return (
      <div className="client-invoices-xero client-create-invoice-page p-4">
        <p className="text-muted mb-0">
          You do not have permission to view commission invoices.
        </p>
      </div>
    );
  }

  if (!clientId || !resolvedInvoiceId) {
    return (
      <div className="client-invoices-xero client-create-invoice-page">
        <p className="text-muted">Invoice not found.</p>
      </div>
    );
  }

  const invoiceNumber =
    existingInvoice?.invoice_number ||
    existingInvoice?.invoice_no ||
    resolvedInvoiceId;

  return (
    <div className="client-invoices-xero client-create-invoice-page">
      <div className="client-invoices-xero__breadcrumb">
        {isFinanceInvoicesReturnTo(returnTo) ? (
          <>
            <span>Manage Finance</span>
            <span aria-hidden="true">›</span>
            <Link to={returnTo}>Invoices</Link>
          </>
        ) : (
          <>
            <Link to="/commissions-invoices">Commissions</Link>
            <span aria-hidden="true">›</span>
            <Link to={returnTo}>Detail</Link>
          </>
        )}
        <span aria-hidden="true">›</span>
        <span>Edit invoice {invoiceNumber}</span>
      </div>

      <InvoiceCreateWizard
        key={`${resolvedInvoiceId}-${commissionShellStatus}`}
        show
        pageMode
        pageLoading={loading}
        commissionShellStatus={commissionShellStatus}
        editMode
        onClose={handleClose}
        onUpdate={handleUpdate}
        onSaveComplete={
          isDraftLikeCommissionInvoice ? handleSaveComplete : undefined
        }
        onAfterSaveAndAnother={
          isDraftLikeCommissionInvoice ? handleAfterSaveAndAnother : undefined
        }
        onInvoiceEmailSent={() =>
          fetchInvoiceNotes(resolvedInvoiceId, { silent: true })
        }
        existingInvoice={
          existingInvoice ||
          normalizeFinanceInvoiceFromRecord({ id: resolvedInvoiceId })
        }
        clientData={clientData}
        invoiceSetting={invoiceSetting}
        invoiceCatalog={invoiceCatalog}
        onCatalogLoaded={handleCatalogLoaded}
        saving={saving}
        discountFeeRules={discountFeeRules}
        existingInvoicesForNumbering={[]}
        clientApplications={[]}
        loadingInvoice={loading}
        invoiceDataLoadedFromPage
        installmentSuggestionSource={installmentSuggestionSource}
        suggestionLinesOnly
        commissionPlanMeta={commissionPlanMeta}
        billToProfile={billToProfile}
        commissionProviderId={
          Number(
            suggestionMeta.providerId ||
              existingInvoice?.provider_id ||
              existingInvoice?.providerId ||
              "",
          ) || null
        }
        onProviderBillToProfileChange={(patch) => {
          setProviderBillTo((prev) => ({
            ...(prev || {}),
            ...patch,
          }));
        }}
        onBillToClientSnapshotUpdate={(client_snapshot) => {
          setExistingInvoice((prev) =>
            prev ? { ...prev, client_snapshot } : prev,
          );
        }}
        onInvoiceSettingChange={(nextSetting) => {
          setInvoiceSetting(nextSetting);
        }}
        onAddInvoiceNote={handleAddInvoiceNote}
        addingInvoiceNote={addingInvoiceNote}
        invoiceNotes={invoiceNotes}
        loadingInvoiceNotes={loadingInvoiceNotes}
        onHistoryVisible={() => fetchInvoiceNotes(resolvedInvoiceId)}
        onSaveFinanceInvoicePayment={
          isDraftLikeCommissionInvoice ? null : handleAddFinanceInvoicePayment
        }
        onSaveFinanceInvoiceBulkPayment={
          isDraftLikeCommissionInvoice
            ? null
            : handleAddFinanceInvoiceBulkPayment
        }
        savingFinanceInvoicePayment={savingFinancePayment}
        onReverseFinanceInvoicePayment={handleReverseFinanceInvoicePayment}
        reversingFinanceInvoicePayment={reversingFinancePayment}
        onVoidInvoice={
          isDraftLikeCommissionInvoice ? undefined : handleVoidInvoice
        }
        voidingInvoice={voidingInvoice}
        onMoveInvoiceToDraft={
          isDraftLikeCommissionInvoice ? undefined : handleMoveInvoiceToDraft
        }
        movingInvoiceToDraft={movingInvoiceToDraft}
        invoiceFollowupConfig={invoiceFollowupConfig}
      />
    </div>
  );
}

export default CommissionEditInvoice;
